import React from 'react';
import { useTranslation } from 'react-i18next';
import TranslationNamespaces from '../../locales/TranslationNamespaces';
import { useApi } from '../../state/ApiContext';
import { useCache } from '../../state/CachingContext';
import SiteHelper from '../../helpers/SiteHelper';
import HeaderWithDescription from '../Shared/HeaderWithDescription';
import CenteredQButton from '../Shared/CenteredQButton';
import FlexContainer from '../Shared/FlexContainers/FlexContainer';
import PageError from '../Shared/PageError';
import { AxiosError } from 'axios';
import CenteredDescription from '../Shared/CenteredDescription';

const RegisterVerification = (): JSX.Element => {
  const namespace = TranslationNamespaces.registerVerification;
  const { t } = useTranslation(namespace);
  const { postRequest } = useApi();
  const { getCache, clearCache } = useCache();
  const session = getCache();

  // const [session, setSession] = React.useState<Record<string, unknown>>();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [viewState, setViewState] = React.useState<
    | undefined
    | 'error'
    | 'nothingToVerify'
    | 'expiredPincode'
    | 'expiredPincodeRequested'
    | 'errorExpiredPincodeRequested'
    | 'success'
  >(undefined);

  const bootstrapAsync = async (): Promise<void> => {
    if (session && session.email && session.token) {
      const postData = { identifier: session.email, pincode: session.token };

      await postRequest('RegisterAccount/Verify', postData, undefined, ['codeExpired'])
        .then(() => {
          SiteHelper.stripQueryString();
          setViewState('success');
          clearCache('token');
        })
        .catch((error: AxiosError) => {
          if (error.response?.data.errors?.global?.includes('codeExpired')) {
            setViewState('expiredPincode');
          } else {
            setViewState('error');
          }
        });
    } else {
      setViewState('nothingToVerify');
    }
  };

  React.useEffect(() => {
    bootstrapAsync();
  }, []);

  const resendPincode = async (): Promise<void> => {
    if (session?.email) {
      const postData = {
        identifier: session.email,
        invitationGuid: session.invitationguid,
      };
      await postRequest('RegisterAccount/ResendAccountVerification', postData, setIsSubmitting)
        .then(() => setViewState('expiredPincodeRequested'))
        .catch(() => setViewState('errorExpiredPincodeRequested'));
    }
  };

  return (
    <>
      {viewState && (
        <>
          {viewState === 'error' && (
            <PageError code='500' message='accountVerificationError' translationNamespace={namespace} />
          )}
          {viewState === 'nothingToVerify' && <PageError code='400' message='accountVerificationNothingToVerify' />}
          {viewState === 'expiredPincode' && (
            <>
              <HeaderWithDescription
                headerSuffix='ExpiredPincode'
                description='expiredPincodeDescription'
                translationNamespace={namespace}
              />

              <FlexContainer>
                <CenteredQButton onClick={resendPincode} isLoading={isSubmitting} color='secondary' fullWidth>
                  {t('request')}
                </CenteredQButton>
              </FlexContainer>
            </>
          )}
          {viewState === 'expiredPincodeRequested' && (
            <>
              <HeaderWithDescription
                headerSuffix='ExpiredPincodeRequested'
                description='expiredPincodeRequestedDescription'
                translationNamespace={namespace}
              />

              <FlexContainer>
                <CenteredDescription description='canCloseWindow' translationNamespace={namespace} />
              </FlexContainer>
            </>
          )}
          {viewState === 'errorExpiredPincodeRequested' && (
            <PageError message='wentWrong' translationNamespace={namespace} />
          )}
          {viewState === 'success' && (
            <>
              <HeaderWithDescription
                headerSuffix='Complete'
                description='completeDescription'
                translationNamespace={namespace}
              />

              <FlexContainer>
                <CenteredQButton to='/' color='secondary' fullWidth>
                  {t('login')}
                </CenteredQButton>
              </FlexContainer>
            </>
          )}
        </>
      )}
    </>
  );
};

export default RegisterVerification;
